/* eslint-disable no-unused-vars */
/* eslint-disable multiline-ternary */
/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import {
  useLocation,
  useNavigate,
  Routes,
  Route,
  Outlet,
  Navigate,
} from 'react-router-dom';
import {
  Box, Stack, Flex, HStack, IconButton,
  useDisclosure, useColorModeValue, useColorMode, ButtonGroup, Spinner
} from '@chakra-ui/react';
import { Select } from "chakra-react-select";
import { useApps, useAudits, useForm, useChildRecords } from './_sys/queries';
import { getObservationsLayout } from './_sys/formObservationLayout';
import SignOutButton from './components/SignOutButton';
import { TRCIcon } from './components/Icons';
import NavDrawer from './components/NavDrawer';
import { DrawerButton } from './components/DrawerButton';
import { OAuthButton } from './components/OathButton';
import { RiMoonFill, RiSunFill } from 'react-icons/ri';
import ErrorBoundary from './components/ErrorBoundary';
import { skipAuth } from './authConfig';
const FeaturesList = lazy(() => import('./routes/FeaturesList'));
const BasicStatistics = lazy(() => import('./routes/BasicStatistics'));
const Editor = lazy(() => import('./routes/Editor'));
const Admin = lazy(() => import('./routes/Admin'));
const Contact = lazy(() => import('./routes/Contact'));
const SignIn = lazy(() => import('./routes/SignIn'));


function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Stable id for a site visit row: nested `2d24` Query rows match `_parent_id` to this value, and REST
 * uses the same id on `form_values[parentKey][].id`.
 * On Fulcrum child repeatable tables, `_record_id` is often the **top-level form** record id (repeated
 * for every row under one audit), not the repeatable row id—do not prefer it over `id`.
 */
function rowIdFromSiteVisitRow(row) {
  return row.id ?? row._child_record_id ?? row._record_id;
}

export default function App() {

  let query = useQuery();

  const [record, setRecord] = useState(query.get('record') ?? null);

  return (
    <>
      <ErrorBoundary>
        <Root record={record} setRecord={setRecord} />
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/" element={
              <RequireAuth >
                <FeaturesList />
              </RequireAuth>} />
            <Route path="signin" element={<SignIn />} />
            <Route path="editor" element={
              <RequireAuth >
                <Editor record={record} />
              </RequireAuth>} />
            <Route path="reports" element={
              <RequireAuth>
                <BasicStatistics record={record} />
              </RequireAuth>} />
            <Route path="admin" element={
              <RequireAuth>
                <Admin />
              </RequireAuth>} />
            <Route path="contact" element={<Contact />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

function RequireAuth({ children }) {
  const isAuthenticated = useIsAuthenticated();
  let location = useLocation();
  if (skipAuth) {
    return children;
  }
  // console.log(location.pathname)
  if (!isAuthenticated) {
    console.log(isAuthenticated)
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  return children;
}

function Root({ setRecord }) {
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode, toggleColorMode } = useColorMode();
  const [auditRecord, setAuditRecord] = useState('');
  const [siteVisitRecord, setSiteVisitRecord] = useState('');
  const isAuthenticated = useIsAuthenticated();
  const effectiveAuth = skipAuth || isAuthenticated;
  const DrawerButtonDisclosure = useDisclosure();

  const btnRef = React.useRef()
  // get all apps with 'Compliance Audit' in their name
  const apps = useApps();
  // get all audit records for selected parent app
  const audits = useAudits(query.get('form'));
  const formId = query.get('form');
  const recordId = query.get('record');
  const form = useForm(formId);
  const obsLayout = useMemo(
    () => (form.data ? getObservationsLayout(form.data, formId) : { isNested: false, parentRepeatableKey: null }),
    [form.data, formId]
  );
  const siteVisits = useChildRecords(
    formId,
    recordId,
    obsLayout.isNested ? obsLayout.parentRepeatableKey : null
  );

  useEffect(() => {
    if (query.get('record')) {
      const match = audits?.data ? audits?.data.find(audit => audit?._record_id === query.get('record')) : null;
      const auditMatch = match ? {
        value: match?._record_id,
        label: match?._title
      } : ''
      setAuditRecord(auditMatch)
    } else {
      setAuditRecord('')
    }

  }, [audits.isFetched])

  useEffect(() => {
    const sv = query.get('siteVisit');
    if (sv && siteVisits?.data) {
      const match = siteVisits.data.find((row) => rowIdFromSiteVisitRow(row) === sv);
      setSiteVisitRecord(match ? {
        value: rowIdFromSiteVisitRow(match),
        label: match?._title ?? match?._record_id
      } : '');
    } else if (!sv) {
      setSiteVisitRecord('');
    }
  }, [query, siteVisits?.isFetched, siteVisits?.data])

  const bg = useColorModeValue('gray.100', 'gray.900');

  if (apps.isFetched) {
    return (
      <Box w='full'>
        <Box bg={bg} px={4}>
          <Flex h="auto" alignItems="center" justifyContent="space-between">
            {effectiveAuth
              ? (
                <Stack direction={['column', 'row']} spacing={8} alignItems="center">
                  <ButtonGroup>
                    <IconButton
                      bg={bg}
                      icon={<TRCIcon boxSize="20" />}
                      onClick={() => navigate(`/`)}
                    />
                  </ButtonGroup>

                  <Stack
                    as="nav"
                    direction={['column', 'row']}
                    spacing={6}
                    display={['/editor', '/reports'].includes(location.pathname) ? 'flex' : 'none'}
                  >
                    <ButtonGroup style={{ zIndex: 11 }}>
                      <Box w={250}>
                        <Select
                          isLoading={!apps.data}
                          disabled={!apps.data}
                          loadingText='Loading'
                          placeholder='Search Audit App'
                          loadingMessage='Loading Apps'
                          isSearchable
                          defaultValue={() => {
                            let match = apps?.data ? apps?.data.find(app => app?.form_id === query.get('form')) : null;
                            return match ? {
                              value: match?.form_id,
                              label: match?.name
                            } : ''
                          }
                          }
                          size="sm"
                          options={apps.data ? apps.data.map(app => (
                            {
                              value: app?.form_id,
                              label: app?.name
                            }
                          )) : []
                          }
                          onChange={(e) => {
                            console.log(e)
                            navigate(`?form=${e.value}`)
                          }}
                        />
                      </Box>

                      <Box w={250}>
                        <Select
                          isLoading={audits.isLoading}
                          disabled={!audits.isSuccess}
                          loadingText='Loading'
                          colorScheme='teal' variant='solid'
                          placeholder='Search Audit Records'
                          loadingMessage='Loading Audits'
                          isSearchable
                          size="sm"
                          value={auditRecord}
                          options={audits.data ? audits.data.map(audit => (
                            {
                              value: audit?._record_id,
                              label: audit?._title
                            }
                          )) : []
                          }
                          onChange={(e) => {
                            setRecord(e.value)
                            setAuditRecord(e)
                            navigate(`?form=${formId}&record=${e.value}`)
                          }}
                        />
                      </Box>

                      {obsLayout.isNested ? (
                        <Box w={250}>
                          <Select
                            isLoading={siteVisits.isLoading}
                            disabled={!siteVisits.isSuccess || !recordId}
                            loadingText='Loading'
                            colorScheme='teal'
                            variant='solid'
                            placeholder='Search Site Visits'
                            loadingMessage='Loading Site Visits'
                            isSearchable
                            size="sm"
                            value={siteVisitRecord}
                            options={siteVisits.data ? siteVisits.data.map((row) => ({
                              value: rowIdFromSiteVisitRow(row),
                              label: row._title ?? row._record_id
                            })) : []}
                            onChange={(e) => {
                              setSiteVisitRecord(e)
                              navigate(`?form=${formId}&record=${recordId}&siteVisit=${e.value}`)
                            }}
                          />
                        </Box>
                      ) : null}
                    </ButtonGroup>
                  </Stack>
                </Stack>
              )
              : <HStack display={{ base: 'none', md: 'flex' }} spacing={8} alignItems="center">
                <Box>
                  <IconButton
                    icon={<TRCIcon boxSize="20" />}
                    onClick={() => navigate(`/`)}
                  />
                </Box>
                <HStack
                  as="nav"
                  spacing={6}
                  display={{ base: 'none', md: 'flex' }}
                >
                </HStack>
              </HStack>}
            <NavDrawer Disclosure={DrawerButtonDisclosure} btnRef={btnRef} />
            <ButtonGroup variant='outline' alignItems="center" spacing='1'>
              <IconButton icon={colorMode === 'light' ? <RiMoonFill /> : <RiSunFill />} onClick={toggleColorMode} />
              {skipAuth ? null : isAuthenticated ? <SignOutButton /> : <OAuthButton />}
              {effectiveAuth ? <DrawerButton Disclosure={DrawerButtonDisclosure} btnRef={btnRef} /> : null}
            </ButtonGroup>
          </Flex>
        </Box>
        <Outlet />
      </Box >
    );
  }

  return null

}
